import type { ReactNode } from "react";
import type { FieldError } from "react-hook-form";

import * as React from "react";
import { useFormContext } from "react-hook-form";

import { cn } from "../../utils";
import { Button } from "./button";
import { FormField, FormItem, FormMessage } from "./form";
import { InputGroup, InputGroupAddon, InputGroupTextarea } from "./input-group";
import { Skeleton } from "./skeleton";
import { Spinner } from "./spinner";

interface CommentInputContextValue {
  hasValue: boolean;
}

const CommentInputContext = React.createContext<CommentInputContextValue>({
  hasValue: false,
});

const useCommentInputContext = () => React.useContext(CommentInputContext);

interface CommentsProps {
  children: ReactNode;
  className?: string;
}

const Comments = ({ children, className }: CommentsProps) => {
  return (
    <div className={cn("af-flex af-flex-col af-gap-4 af-p-4", className)}>
      {children}
    </div>
  );
};

interface CommentProps {
  children: ReactNode;
  className?: string;
}

const Comment = ({ children, className }: CommentProps) => {
  return (
    <div
      className={cn("af-flex af-flex-row af-items-start af-gap-2", className)}
    >
      {children}
    </div>
  );
};

interface CommentContentProps {
  children: ReactNode;
  className?: string;
}

const CommentContent = ({ children, className }: CommentContentProps) => {
  return (
    <div
      className={cn(
        "af-flex af-min-w-0 af-flex-1 af-flex-col af-gap-3",
        className,
      )}
    >
      {children}
    </div>
  );
};

interface CommentHeaderProps {
  children: ReactNode;
  className?: string;
}

const CommentHeader = ({ children, className }: CommentHeaderProps) => {
  return (
    <div
      className={cn(
        "af-flex af-flex-row af-items-start af-justify-between af-gap-2",
        className,
      )}
    >
      {children}
    </div>
  );
};

interface CommentActionsProps {
  children: ReactNode;
  className?: string;
}

const CommentActions = ({ children, className }: CommentActionsProps) => {
  return (
    <div
      className={cn("af-flex af-flex-row af-items-center af-gap-1", className)}
    >
      {children}
    </div>
  );
};

interface CommentSkeletonProps {
  className?: string;
}

const CommentSkeleton = ({ className }: CommentSkeletonProps) => {
  return (
    <div className={className ?? "af-flex af-flex-row af-gap-2 af-align-top"}>
      <Skeleton className="af-size-8 af-rounded-full" />
      <div className="af-flex af-flex-1 af-flex-col af-gap-3">
        <div className="af-flex af-flex-col af-gap-2">
          <Skeleton className="af-h-4 af-w-32" />
          <Skeleton className="af-h-3 af-w-40" />
        </div>
        <div className="af-flex af-flex-col af-gap-2">
          <Skeleton className="af-h-4 af-w-full" />
          <Skeleton className="af-h-4 af-w-5/6" />
        </div>
      </div>
    </div>
  );
};

const AutoResizeCommentTextarea = ({
  field,
  placeholder,
  error,
  className,
}: {
  field: {
    ref: (instance: HTMLTextAreaElement | null) => void;
    value?: string;
    onChange: (...args: unknown[]) => void;
    onBlur: (...args: unknown[]) => void;
    name: string;
  };
  placeholder?: string;
  error?: FieldError;
  className?: string;
}) => {
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);

  const resize = React.useCallback(() => {
    const textarea = textareaRef.current;

    if (!textarea) {
      return;
    }

    textarea.style.height = "auto";

    const maxHeight = parseFloat(window.getComputedStyle(textarea).maxHeight);
    const scrollHeight = textarea.scrollHeight;

    textarea.style.height = `${Math.min(
      scrollHeight,
      Number.isFinite(maxHeight) ? maxHeight : scrollHeight,
    )}px`;
  }, []);

  React.useLayoutEffect(() => {
    resize();
  }, [field.value, resize]);

  return (
    <InputGroupTextarea
      {...field}
      ref={(node) => {
        textareaRef.current = node;
        field.ref(node);
      }}
      aria-invalid={error ? "true" : "false"}
      onInput={resize}
      placeholder={placeholder}
      className={cn(
        "af-min-h-[80px] af-flex-none af-overflow-hidden",
        className,
      )}
    />
  );
};

interface CommentInputProps {
  children: ReactNode;
  className?: string;
}

const CommentInput = ({ children, className }: CommentInputProps) => {
  return (
    <div
      className={cn(
        "af-flex af-w-full af-flex-row af-items-start af-gap-2 af-text-sm",
        className,
      )}
    >
      {children}
    </div>
  );
};

interface CommentInputContentProps {
  children: ReactNode;
  className?: string;
}

const CommentInputContent = ({
  children,
  className,
}: CommentInputContentProps) => {
  const form = useFormContext();
  const hasValue = Boolean(form.watch("content"));

  return (
    <CommentInputContext.Provider value={{ hasValue }}>
      <div className={cn("af-flex af-w-full af-flex-col af-gap-2", className)}>
        {children}
      </div>
    </CommentInputContext.Provider>
  );
};

interface CommentInputFieldProps {
  placeholder?: string;
  maxLength?: number;
  className?: string;
  textareaClassName?: string;
}

const CommentInputField = ({
  placeholder = "Add a comment...",
  maxLength,
  className,
  textareaClassName,
}: CommentInputFieldProps) => {
  return (
    <FormItem className={className}>
      <FormField
        name="content"
        render={({ field, fieldState }) => (
          <FormItem className="af-w-full">
            <InputGroup>
              <AutoResizeCommentTextarea
                field={field}
                placeholder={placeholder}
                error={fieldState.error}
                className={textareaClassName}
              />
              {maxLength && (
                <InputGroupAddon align="block-end">
                  <span className="af-ml-auto af-text-xs af-text-muted-foreground">
                    {(field.value as string)?.length}/{maxLength}
                  </span>
                </InputGroupAddon>
              )}
            </InputGroup>
            <FormMessage />
          </FormItem>
        )}
      />
    </FormItem>
  );
};

const CommentInputActions = ({ className }: { className?: string }) => {
  const form = useFormContext();
  const { hasValue } = useCommentInputContext();

  if (!hasValue) {
    return null;
  }

  return (
    <div
      className={cn(
        "af-flex af-flex-row af-justify-end af-gap-2 af-px-3",
        className,
      )}
    >
      <Button
        variant="ghost"
        type="button"
        size="sm"
        onClick={() => {
          form.reset();
        }}
      >
        Cancel
      </Button>
      <Button size="sm" type="submit" disabled={!form.formState.isValid}>
        {form.formState.isSubmitting && <Spinner className="af-size-4" />}
        Submit
      </Button>
    </div>
  );
};

export {
  Comment,
  CommentActions,
  CommentContent,
  CommentHeader,
  CommentInput,
  CommentInputActions,
  CommentInputContent,
  CommentInputField,
  Comments,
  CommentSkeleton,
};
